import { Injectable, OnDestroy } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class KycAuthService implements OnDestroy {
  private accessToken: string | null = null;
  private locale: string = 'en';
  private theme: 'light' | 'dark' | 'system' = 'light';
  private platform: 'ios' | 'android' | null = null;
  private requiredVersion: number | null = null;
  private bootstrapReceived: boolean = false;

  setBootstrapData(data: {
    accessToken: string;
    locale?: string;
    theme?: string;
    platform?: string;
    requiredVersion?: number;
  }) {
    this.accessToken = data.accessToken || null;

    // Localization mapping
    const rawLocale = data.locale || 'en-US';
    if (rawLocale.startsWith('ar')) {
      this.locale = 'ar';
    } else {
      this.locale = 'en';
    }

    // Theme normalization
    const rawTheme = data.theme || 'light';
    if (rawTheme === 'dark' || rawTheme === 'system') {
      this.theme = rawTheme;
    } else {
      this.theme = 'light';
    }

    // Platform normalization
    const rawPlatform = (data.platform || '').toLowerCase();
    if (rawPlatform === 'ios' || rawPlatform === 'android') {
      this.platform = rawPlatform as 'ios' | 'android';
    } else {
      this.platform = null;
    }

    this.requiredVersion = data.requiredVersion || null;
    this.bootstrapReceived = true;
  }

  isBootstrapped(): boolean {
    if (this.bootstrapReceived && !!this.accessToken) {
      return true;
    }
    // Fallback for local browser testing:
    // If the browser already has an accessToken in localStorage (due to logged-in session), we can use it!
    const localToken = localStorage.getItem('accessToken');
    if (localToken) {
      if (!this.accessToken) {
        this.accessToken = localToken;
        this.bootstrapReceived = true;
        const language = localStorage.getItem('language') || 'en';
        this.locale = language.startsWith('ar') ? 'ar' : 'en';
        this.theme = 'light';
        this.platform = null;
      }
      return true;
    }
    return false;
  }

  getToken(): string | null {
    const raw = this.accessToken || localStorage.getItem('accessToken');
    if (raw && raw.startsWith('"') && raw.endsWith('"')) {
      try {
        return JSON.parse(raw);
      } catch {
        return raw.replace(/^"|"$/g, '');
      }
    }
    return raw;
  }

  getLocale(): string {
    return this.locale;
  }

  getTheme(): 'light' | 'dark' | 'system' {
    return this.theme;
  }

  getPlatform(): 'ios' | 'android' | null {
    return this.platform;
  }

  getRequiredVersion(): number | null {
    return this.requiredVersion;
  }

  clear() {
    this.accessToken = null;
    this.locale = 'en';
    this.theme = 'light';
    this.platform = null;
    this.requiredVersion = null;
    this.bootstrapReceived = false;
  }

  ngOnDestroy() {
    this.clear();
  }
}
